<!-- Incoming Parcel Detail View Component -->
<script setup>
definePage({
  meta: {
    requiresAuth: true,
    permissions: ['view-GB-parcels', 'view-GD-parcels', 'view-JP-parcels'],
    title: 'Parcel Details',
  },
})

import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useSnackbar } from '@/composables/useSnackbar'
import axios from 'axios'

import { useAbility } from '@/plugins/casl/composables/useAbility'
import { getStatusColor } from '@/utils/statusUtils'
import { formatCurrency } from '@/utils/currencyUtils'

const { t, te } = useI18n()
const route = useRoute()
const router = useRouter()
const { error } = useSnackbar()

const {
  canEditParcels,
  canDeleteParcels,
  canAccessTeam,
  isAdmin,
  getTeamBadgeColor,
  getTeamDisplayName,
} = useAbility()

// State
const parcel = ref(null)
const loading = ref(true)
const activeTab = ref(0)
const imageViewer = ref({ open: false, url: null })
const claimDialogOpen = ref(false)

const parcelId = computed(() => route.params.id)

const canAccessParcel = computed(() => {
  if (!parcel.value) return false
  if (isAdmin.value) return true
  if (parcel.value.team?.code) return canAccessTeam(parcel.value.team.code)
  
  return false
})

const canEditSpecificParcel = computed(() => {
  if (!canAccessParcel.value) return false
  if (parcel.value?.team?.code) return canEditParcels(parcel.value.team.code)
  
  return false
})

// Convenience derived values
const containerInfo = computed(() => parcel.value?.container_info)
const invoiceInfo = computed(() => parcel.value?.invoice_info)
const packingInfo = computed(() => parcel.value?.packing_info)
const arrivalInspection = computed(() => parcel.value?.arrival_inspection)
const clientGroupId = computed(() => arrivalInspection.value?.client_arrival_group_id)
const statuses = computed(() => parcel.value?.statuses || [])
const images = computed(() => parcel.value?.images || [])

const statusLabel = key => (te(`shipment.statuses.${key}`) ? t(`shipment.statuses.${key}`) : key)

// Format a dimensions row: L × W × H (cm) — returns '—' if all zero.
const fmtDims = (l, w, h) => {
  const n = v => (v == null || Number(v) === 0 ? '—' : Number(v))
  if (n(l) === '—' && n(w) === '—' && n(h) === '—') return '—'
  
  return `${n(l)} × ${n(w)} × ${n(h)} cm`
}

const fmtWeight = w => (w == null || Number(w) === 0 ? '—' : `${Number(w).toFixed(2)} kg`)
const fmtVolume = v => (v == null || Number(v) === 0 ? '—' : `${Number(v).toFixed(4)} m³`)

const fmtDate = iso => {
  if (!iso) return '—'
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

// Three measurement columns: from PL / on arrival / counter verified.
const measurementColumns = computed(() => {
  const p = parcel.value
  if (!p) return []
  const insp = arrivalInspection.value
  
  return [
    {
      key: 'original',
      title: t('shipment.original_measurements'),
      hint: t('shipment.from_packing_list'),
      color: 'primary',
      dims: fmtDims(p.length, p.width, p.height),
      weight: fmtWeight(p.weight),
      volume: fmtVolume(p.volume),
    },
    {
      key: 'arrival',
      title: t('shipment.arrival_measurements'),
      hint: t('shipment.tana_warehouse'),
      color: 'success',
      dims: insp ? fmtDims(insp.arrival_length, insp.arrival_width, insp.arrival_height) : '—',
      weight: insp ? fmtWeight(insp.arrival_weight) : '—',
      volume: insp ? fmtVolume(insp.arrival_volume) : '—',
    },
    {
      key: 'counter',
      title: t('shipment.counter_measurements'),
      hint: t('shipment.on_dispute'),
      color: 'info',
      dims: insp ? fmtDims(insp.counter_length, insp.counter_width, insp.counter_height) : '—',
      weight: insp ? fmtWeight(insp.counter_weight) : '—',
      volume: insp ? fmtVolume(insp.counter_volume) : '—',
    },
  ]
})

const fetchParcel = async () => {
  try {
    loading.value = true

    const response = await axios.get(`/shipment/incoming-parcels/${parcelId.value}`)
    if (response.data && response.data.id) {
      parcel.value = response.data
      await nextTick()
      if (!canAccessParcel.value) {
        error('Access denied: You cannot view this parcel')
        router.push('/shipment/incoming-parcels')
        
        return
      }
    } else {
      throw new Error('Invalid parcel data received')
    }
  } catch (err) {
    if (err.response?.status === 404) error('Parcel not found')
    else if (err.response?.status === 403) error('Access denied: You cannot view this parcel')
    else error('Failed to load parcel details')
    router.push('/shipment/incoming-parcels')
  } finally {
    loading.value = false
  }
}

const editParcel = () => router.push(`/shipment/incoming-parcels/${parcelId.value}/edit`)
const goBack = () => router.push('/shipment/incoming-parcels')

const openImage = url => {
  imageViewer.value = { open: true, url }
}

onMounted(() => fetchParcel())

// Re-fetch when the `:id` route param changes. Without this, clicking
// another parcel from Cmd+K global search (or any in-page navigation
// to a sibling parcel) keeps the page mounted with the previous parcel's
// data — the URL updates but the content doesn't.
watch(parcelId, (newId, oldId) => {
  if (newId && newId !== oldId) {
    parcel.value = null
    fetchParcel()
  }
})
</script>

<template>
  <div>
    <!-- Loading State -->
    <VCard v-if="loading">
      <VCardText class="text-center pa-8">
        <VProgressCircular
          indeterminate
          color="primary"
        />
        <div class="mt-4">
          {{ t('common.loading') }}...
        </div>
      </VCardText>
    </VCard>

    <div v-else-if="parcel">
      <!-- Header -->
      <VCard class="mb-6">
        <VCardText>
          <div class="d-flex justify-space-between align-center flex-wrap gap-4">
            <div class="d-flex align-center gap-4">
              <VBtn
                variant="text"
                icon
                @click="goBack"
              >
                <VIcon icon="tabler-arrow-left" />
              </VBtn>

              <div>
                <h1 class="text-h4 mb-1">
                  {{ t('shipment.parcel') }} {{ parcel.barcode }}
                </h1>
                <div class="d-flex align-center gap-2 flex-wrap">
                  <ParcelStatusChip
                    :status="parcel.status_key || parcel.status"
                    size="small"
                  />

                  <TeamBadge
                    :team="parcel.team"
                    size="small"
                  />

                  <VChip
                    :color="parcel.transport === 0 ? 'info' : 'primary'"
                    size="small"
                    variant="tonal"
                    :prepend-icon="parcel.transport === 0 ? 'tabler-plane' : 'tabler-ship'"
                  >
                    {{ parcel.transport === 0 ? t('shipment.by_plane') : t('shipment.by_ship') }}
                  </VChip>

                  <!-- Container chip — clickable to container detail -->
                  <VChip
                    v-if="containerInfo?.container_id"
                    size="small"
                    variant="outlined"
                    color="info"
                    prepend-icon="tabler-ship"
                    :to="{ name: 'history-containers-detail', params: { containerId: containerInfo.container_id } }"
                  >
                    {{ containerInfo.container_reference_code }}
                  </VChip>

                  <!-- Client group chip — clickable to warehouse groups page -->
                  <VChip
                    v-if="clientGroupId && containerInfo?.container_id"
                    size="small"
                    variant="outlined"
                    color="secondary"
                    prepend-icon="tabler-users"
                    :to="{ name: 'shipment-warehouse-client-groups', params: { containerId: containerInfo.container_id } }"
                  >
                    {{ t('shipment.group') }} #{{ clientGroupId }}
                  </VChip>

                  <!-- Bag code -->
                  <VChip
                    v-if="parcel.bag_code"
                    size="small"
                    variant="tonal"
                    color="warning"
                    prepend-icon="tabler-tag"
                  >
                    {{ parcel.bag_code }}
                  </VChip>
                </div>
              </div>
            </div>

            <div class="d-flex gap-2 flex-wrap">
              <!--
                Claim / Reassign — always available (server gates the
                actual write to admin / superadmin / team-manager).
                When the parcel is unassigned the label flips to
                "Attribuer" with the primary color; once attached it
                says "Réassigner" in warning to signal the audited
                second-thought action. 
              -->
              <VBtn
                v-if="canAccessParcel"
                :color="parcel?.customer ? 'warning' : 'primary'"
                variant="tonal"
                @click="claimDialogOpen = true"
              >
                <VIcon
                  start
                  :icon="parcel?.customer ? 'tabler-arrow-right' : 'tabler-link'"
                />
                {{ parcel?.customer ? t('claim_dialog.reassign_action') : t('claim_dialog.claim_action') }}
              </VBtn>

              <VBtn
                v-if="canEditSpecificParcel"
                color="primary"
                variant="elevated"
                @click="editParcel"
              >
                <VIcon
                  start
                  icon="tabler-edit"
                />
                {{ t('common.edit') }}
              </VBtn>
            </div>
          </div>
        </VCardText>
      </VCard>

      <!-- Invoice card (if invoiced) -->
      <VCard
        v-if="invoiceInfo"
        class="mb-6"
        border
      >
        <VCardText class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div class="d-flex align-center gap-3">
            <VAvatar
              color="primary"
              variant="tonal"
              size="40"
              rounded="lg"
            >
              <VIcon icon="tabler-file-invoice" />
            </VAvatar>
            <div>
              <div class="text-caption text-disabled">
                {{ t('shipment.invoice') }}
              </div>
              <div class="d-flex align-center gap-2 flex-wrap">
                <span class="font-weight-medium">{{ invoiceInfo.quote_number }}</span>
                <span
                  v-if="invoiceInfo.external_invoice_number"
                  class="text-caption text-disabled"
                >
                  · {{ invoiceInfo.external_invoice_number }}
                </span>
                <VChip
                  size="x-small"
                  variant="tonal"
                  :color="invoiceInfo.status === 'paid' ? 'success' : (invoiceInfo.status === 'cancelled' ? 'error' : 'info')"
                >
                  {{ statusLabel(invoiceInfo.status) }}
                </VChip>
              </div>
            </div>
          </div>
          <div class="d-flex align-center gap-3">
            <span class="font-weight-medium">
              {{ formatCurrency(invoiceInfo.total_amount, invoiceInfo.currency || 'MGA') }}
            </span>
            <VBtn
              variant="tonal"
              color="primary"
              size="small"
              :to="{ name: 'shipment-warehouse-invoices-view', params: { invoiceId: invoiceInfo.id } }"
            >
              {{ t('common.view') }}
            </VBtn>
          </div>
        </VCardText>
      </VCard>

      <!-- Measurement Summary (3 columns) -->
      <VCard class="mb-6">
        <VCardTitle class="d-flex align-center gap-2">
          <VIcon icon="tabler-ruler-measure" />
          {{ t('shipment.measurement_summary') }}
        </VCardTitle>
        <VCardText>
          <VRow>
            <VCol
              v-for="col in measurementColumns"
              :key="col.key"
              cols="12"
              md="4"
            >
              <VCard
                variant="tonal"
                :color="col.color"
                class="measurement-col h-100"
              >
                <VCardText>
                  <div class="text-subtitle-1 font-weight-medium mb-1">
                    {{ col.title }}
                  </div>
                  <div class="text-caption text-uppercase text-disabled mb-3">
                    {{ col.hint }}
                  </div>
                  <div class="d-flex justify-space-between mb-1">
                    <span class="text-caption">{{ t('shipment.dimensions') }}</span>
                    <span class="font-weight-medium">{{ col.dims }}</span>
                  </div>
                  <div class="d-flex justify-space-between mb-1">
                    <span class="text-caption">{{ t('shipment.weight') }}</span>
                    <span class="font-weight-medium">{{ col.weight }}</span>
                  </div>
                  <div class="d-flex justify-space-between">
                    <span class="text-caption">{{ t('shipment.volume') }}</span>
                    <span class="font-weight-medium">{{ col.volume }}</span>
                  </div>
                </VCardText>
              </VCard>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <!-- Tabs -->
      <VTabs
        v-model="activeTab"
        class="mb-4"
      >
        <VTab>{{ t('shipment.basicInfo') }}</VTab>
        <VTab v-if="statuses.length">
          {{ t('shipment.status_history') }}
        </VTab>
        <VTab v-if="parcel.products?.length">
          {{ t('shipment.products') }}
        </VTab>
        <VTab v-if="images.length">
          {{ t('shipment.images') }}
        </VTab>
      </VTabs>

      <VWindow v-model="activeTab">
        <!-- ── Basic Info ───────────────────────────────────────────── -->
        <VWindowItem>
          <VRow>
            <!-- Parcel Information -->
            <VCol
              cols="12"
              md="6"
            >
              <VCard>
                <VCardTitle>{{ t('shipment.parcelInfo') }}</VCardTitle>
                <VCardText>
                  <VList>
                    <VListItem>
                      <template #prepend>
                        <VIcon
                          color="primary"
                          icon="tabler-barcode"
                        />
                      </template>
                      <VListItemTitle>{{ t('shipment.barcode') }}</VListItemTitle>
                      <VListItemSubtitle>{{ parcel.barcode }}</VListItemSubtitle>
                    </VListItem>

                    <VListItem v-if="parcel.bag_code">
                      <template #prepend>
                        <VIcon
                          color="primary"
                          icon="tabler-package"
                        />
                      </template>
                      <VListItemTitle>{{ t('shipment.bag_code') }}</VListItemTitle>
                      <VListItemSubtitle>{{ parcel.bag_code }}</VListItemSubtitle>
                    </VListItem>

                    <VListItem>
                      <template #prepend>
                        <VIcon
                          color="primary"
                          icon="tabler-calendar"
                        />
                      </template>
                      <VListItemTitle>{{ t('shipment.date') }}</VListItemTitle>
                      <VListItemSubtitle>{{ fmtDate(parcel.date) }}</VListItemSubtitle>
                    </VListItem>

                    <VListItem v-if="parcel.notes">
                      <template #prepend>
                        <VIcon
                          color="primary"
                          icon="tabler-notes"
                        />
                      </template>
                      <VListItemTitle>{{ t('shipment.notes') }}</VListItemTitle>
                      <VListItemSubtitle>{{ parcel.notes }}</VListItemSubtitle>
                    </VListItem>

                    <!--
                      Packing list / outgoing package — trace where the
                      parcel was packed. 
                    -->
                    <VListItem v-if="packingInfo?.packing_list_reference">
                      <template #prepend>
                        <VIcon
                          color="primary"
                          icon="tabler-box"
                        />
                      </template>
                      <VListItemTitle>{{ t('shipment.packing_list') }}</VListItemTitle>
                      <VListItemSubtitle>
                        <RouterLink
                          v-if="packingInfo.packing_list_id"
                          :to="{ name: 'shipment-packing-lists-view', params: { id: packingInfo.packing_list_id } }"
                          class="text-primary text-decoration-none"
                        >
                          {{ packingInfo.packing_list_reference }}
                        </RouterLink>
                        <span v-else>{{ packingInfo.packing_list_reference }}</span>
                      </VListItemSubtitle>
                    </VListItem>

                    <VListItem v-if="packingInfo?.outgoing_package_barcode">
                      <template #prepend>
                        <VIcon
                          color="primary"
                          icon="tabler-box-seam"
                        />
                      </template>
                      <VListItemTitle>{{ t('shipment.outgoing_package') }}</VListItemTitle>
                      <VListItemSubtitle>{{ packingInfo.outgoing_package_barcode }}</VListItemSubtitle>
                    </VListItem>
                  </VList>
                </VCardText>
              </VCard>
            </VCol>

            <!-- Customer + first image -->
            <VCol
              cols="12"
              md="6"
            >
              <VCard class="mb-4">
                <VCardTitle>{{ t('shipment.customer') }}</VCardTitle>
                <VCardText>
                  <div v-if="parcel.customer">
                    <CustomerLink
                      :customer="parcel.customer"
                      size="lg"
                      show-client-id
                    />
                    <div
                      v-if="parcel.customer.phone"
                      class="mt-2 d-flex align-center text-body-2"
                    >
                      <VIcon
                        icon="tabler-phone"
                        size="16"
                        class="me-2"
                      />
                      {{ parcel.customer.phone }}
                    </div>
                  </div>
                  <VAlert
                    v-else
                    type="info"
                    variant="tonal"
                    density="compact"
                  >
                    {{ t('shipment.no_customer_assigned') }}
                  </VAlert>
                </VCardText>
              </VCard>

              <!-- First image inline (rest in Images tab) -->
              <VCard v-if="images.length">
                <VCardTitle class="d-flex align-center gap-2">
                  <VIcon icon="tabler-photo" />
                  {{ t('shipment.preview') }}
                  <VChip
                    v-if="images.length > 1"
                    size="x-small"
                    variant="tonal"
                    class="ms-2"
                  >
                    +{{ images.length - 1 }}
                  </VChip>
                </VCardTitle>
                <VCardText>
                  <VImg
                    :src="images[0].url"
                    aspect-ratio="1.6"
                    cover
                    class="rounded cursor-pointer"
                    @click="openImage(images[0].url)"
                  />
                </VCardText>
              </VCard>
            </VCol>
          </VRow>
        </VWindowItem>

        <!-- ── Status timeline ───────────────────────────────────────── -->
        <VWindowItem v-if="statuses.length">
          <VCard>
            <VCardTitle>{{ t('shipment.status_history') }}</VCardTitle>
            <VCardText>
              <VTimeline
                align="start"
                side="end"
                density="compact"
              >
                <VTimelineItem
                  v-for="s in statuses"
                  :key="s.id"
                  :dot-color="getStatusColor(s.name)"
                  size="small"
                >
                  <div class="d-flex align-center gap-2 flex-wrap">
                    <VChip
                      :color="getStatusColor(s.name)"
                      size="small"
                      variant="tonal"
                    >
                      {{ statusLabel(s.name) }}
                    </VChip>
                    <span class="text-caption text-disabled">
                      {{ fmtDate(s.created_at) }}
                    </span>
                  </div>
                  <div
                    v-if="s.reason"
                    class="text-body-2 mt-1"
                  >
                    {{ s.reason }}
                  </div>
                </VTimelineItem>
              </VTimeline>
            </VCardText>
          </VCard>
        </VWindowItem>

        <!-- ── Products ──────────────────────────────────────────────── -->
        <VWindowItem v-if="parcel.products?.length">
          <VCard>
            <VCardTitle>{{ t('shipment.products') }}</VCardTitle>
            <VCardText>
              <VTable>
                <thead>
                  <tr>
                    <th>{{ t('shipment.product_name') }}</th>
                    <th>{{ t('shipment.quantity') }}</th>
                    <th>{{ t('shipment.detail') }}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="product in parcel.products"
                    :key="product.id"
                  >
                    <td>{{ product.name }}</td>
                    <td>{{ product.quantity }}</td>
                    <td>{{ product.detail || '-' }}</td>
                  </tr>
                </tbody>
              </VTable>
            </VCardText>
          </VCard>
        </VWindowItem>

        <!-- ── Images gallery ────────────────────────────────────────── -->
        <VWindowItem v-if="images.length">
          <VCard>
            <VCardTitle>{{ t('shipment.images') }}</VCardTitle>
            <VCardText>
              <VRow>
                <VCol
                  v-for="image in images"
                  :key="image.id"
                  cols="12"
                  sm="6"
                  md="4"
                >
                  <VImg
                    :src="image.url"
                    aspect-ratio="1"
                    cover
                    class="rounded cursor-pointer elevation-2"
                    @click="openImage(image.url)"
                  />
                </VCol>
              </VRow>
            </VCardText>
          </VCard>
        </VWindowItem>
      </VWindow>

      <!-- Image viewer dialog -->
      <VDialog
        v-model="imageViewer.open"
        max-width="900"
      >
        <VCard>
          <VImg
            :src="imageViewer.url"
            contain
            max-height="80vh"
          />
          <VCardActions>
            <VSpacer />
            <VBtn @click="imageViewer.open = false">
              {{ t('common.close') }}
            </VBtn>
          </VCardActions>
        </VCard>
      </VDialog>

      <!-- Claim / Reassign dialog -->
      <ClaimParcelDialog
        v-model="claimDialogOpen"
        mode="single"
        :parcel-id="parcel?.id"
        :barcode="parcel?.barcode"
        :current-customer-id="parcel?.customer?.id"
        :current-customer-name="parcel?.customer?.name"
        @done="fetchParcel"
      />
    </div>
  </div>
</template>

<style scoped>
.measurement-col {
  min-block-size: 9rem;
}
</style>
